import clsx from 'clsx';
import DOMpurify from 'dompurify';
import { KeyboardEvent, SyntheticEvent, useRef, useState } from 'react';

import { CoreIcon } from '../icon';
import { CoreTextFieldProps } from '../shared/types/text-field.types';

import { ClearAllButton } from './clear-all-button';
import styles from './text-field.module.scss';
import { TrailingAction } from './trailing-action';
import { usePasswordField } from './usePasswordField';

export const getIncrementValue = (value: number, incrementValue: number) => {
  const modulus = Number(value) % incrementValue;

  if (modulus) {
    return Number(value) + (incrementValue - modulus);
  }

  return Number(value) + incrementValue;
};

export const getDecrementValue = (value: number, incrementValue: number) => {
  const modulus = value % incrementValue;

  if (modulus) {
    return value - modulus;
  }

  return value - incrementValue;
};

const CoreTextField = ({
  id,
  label,
  value,
  onValueChanged,
  onKeyDown,
  onDecrementClick,
  onIncrementClick,
  blurHandler,
  focusHandler,
  type = 'text',
  placeholderText = '',
  maskRegEx,
  autoComplete,
  disabled = false,
  readOnly = false,
  incrementValue = 1,
  maxIncrementValue,
  decrementAriaLabel,
  incrementAriaLabel,
  icon,
  iconPosition = 'trailing',
  isHidden = false,
  helpText,
  errorText,
  prefixOnFocus,
  resetValueOnEmptyEntry,
  initialValue,
  ariaLabelledBy,
  inputRef: externalRef,
  maxLength,
  showClearAll,
  role,
  onInputClick,
  onIconClick,
  controlled,
  ...inputProps
}: CoreTextFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [hasBeenEdited, setHasBeenEdited] = useState(false);

  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = externalRef || internalRef;

  const onInputValueChanged = (e: SyntheticEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    const result = maskRegEx?.test(newValue);
    if (result || !maskRegEx || newValue === '') {
      setTempValue(newValue);
      onValueChanged(newValue);
      if (!hasBeenEdited) {
        setHasBeenEdited(true);
      }
    }

    if (!result && !!prefixOnFocus && newValue === '') {
      setTempValue(`${prefixOnFocus}${newValue}`);
      onValueChanged(`${prefixOnFocus}${newValue}`);
    }
  };

  const handleFocus = () => {
    if (!hasBeenEdited) {
      setIsEditing(true);
      setTempValue('');
    }

    if (resetValueOnEmptyEntry && hasBeenEdited) {
      setIsEditing(true);
      setTempValue(value);
    }

    if (!prefixOnFocus) {
      return;
    }

    if (hasBeenEdited && !value.toString().startsWith(prefixOnFocus)) {
      const updatedValue = `${prefixOnFocus}${tempValue}`;
      setTempValue(updatedValue);
      onValueChanged(updatedValue);
      return;
    }

    if (hasBeenEdited && value.toString().startsWith(prefixOnFocus)) {
      setTempValue(value.toString());
      onValueChanged(value.toString());
      return;
    }

    if (!value) {
      setTempValue(prefixOnFocus);
      onValueChanged(prefixOnFocus);
      return;
    }

    setTempValue(prefixOnFocus);
  };

  const handleBlur = () => {
    if (
      resetValueOnEmptyEntry &&
      (value === '' || value === prefixOnFocus) &&
      hasBeenEdited
    ) {
      setHasBeenEdited(false);
      initialValue && setTempValue(initialValue);
      setIsEditing(false);
      initialValue && onValueChanged(initialValue?.toString());
      return;
    }

    if (tempValue === '' && !hasBeenEdited) {
      setTempValue(value);
    }
    setIsEditing(false);
    const sanitizedValue = DOMpurify.sanitize(value?.toString());
    onValueChanged(sanitizedValue);
  };

  const clearAllClicked = () => {
    inputRef?.current?.focus();
    setIsEditing(true);
    setTempValue('');
    onValueChanged('');
  };

  const decrementClicked = () => {
    onDecrementClick?.(id, decrementAriaLabel);

    const newValue = getDecrementValue(Number(value), incrementValue);
    if (newValue < 0) {
      return;
    }
    onValueChanged(newValue.toString());
  };

  const incrementClicked = () => {
    onIncrementClick?.(id, incrementAriaLabel);

    const newValue = getIncrementValue(Number(value), incrementValue);
    if (!maxIncrementValue && maxIncrementValue !== 0) {
      onValueChanged(newValue.toString());
    }

    if (maxIncrementValue && newValue <= maxIncrementValue) {
      onValueChanged(newValue.toString());
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (type === 'quantity') {
      if (event.key === 'ArrowLeft') {
        decrementClicked();
      } else if (event.key === 'ArrowRight') {
        incrementClicked();
      }
    }
  };

  const handleDecrementKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowRight') {
      const incrementButton = document.getElementById(`${id}-increment`);
      incrementButton?.focus();
    }
  };

  const handleIncrementKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowLeft') {
      const decrementButton = document.getElementById(`${id}-decrement`);
      decrementButton?.focus();
    }
  };

  const {
    isPasswordField,
    effectiveType,
    passwordIcon,
    passwordAriaLabel,
    toggle,
  } = usePasswordField(type);

  return (
    <div className={`${styles['text-field-container']}`}>
      <label
        htmlFor={id}
        className={clsx(
          styles['core-form-label'],
          disabled && styles['disabled']
        )}
      >
        {label}
        {ariaLabelledBy && (
          <span className={'sr-only'}>{'. ' + (ariaLabelledBy ?? '')}</span>
        )}
      </label>
      <div
        className={clsx(
          styles['core-form-input-wrapper'],
          type === 'quantity' && styles['text-field-quantity']
        )}
      >
        {iconPosition === 'leading' && icon && (
          <div
            className={clsx(
              styles['text-field-icon'],
              styles['text-field-icon-leading']
            )}
          >
            {icon}
          </div>
        )}
        <input
          {...inputProps}
          id={id}
          data-testid={id}
          role={role}
          type={isHidden ? 'hidden' : effectiveType}
          className={clsx(
            styles['core-text-field'],
            styles['text-field'],
            errorText && styles['core-text-field-error'],
            iconPosition === 'leading' && icon && styles['leading-icon'],
            readOnly && styles['read-only']
          )}
          placeholder={placeholderText}
          required={false}
          value={!controlled && isEditing ? tempValue : value}
          onChange={onInputValueChanged}
          onFocus={(e) => {
            if (focusHandler) {
              focusHandler(e);
              return;
            } else {
              handleFocus();
            }
          }}
          onBlur={blurHandler || handleBlur}
          onKeyDown={onKeyDown || handleKeyDown}
          onClick={onInputClick}
          aria-describedby={errorText ? `${id}-error-text` : ''}
          aria-invalid={errorText ? 'true' : 'false'}
          ref={inputRef}
          autoComplete={autoComplete}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
        />
        <ClearAllButton
          showClearAll={showClearAll}
          value={value}
          isEditing={isEditing}
          tempValue={tempValue}
          icon={icon}
          iconPosition={iconPosition}
          onClear={clearAllClicked}
        />
        <TrailingAction
          isPasswordField={isPasswordField}
          passwordIcon={passwordIcon}
          passwordAriaLabel={passwordAriaLabel}
          toggle={toggle}
          icon={iconPosition === 'trailing' ? icon : undefined}
          onIconClick={onIconClick}
        />

        {type === 'quantity' && (
          <>
            <button
              id={`${id}-decrement`}
              data-testid={`${id}-decrement`}
              className={clsx(
                styles['core-button'],
                styles['core-button-tertiary'],
                styles['core-button-leading']
              )}
              aria-label={decrementAriaLabel}
              onClick={decrementClicked}
              onKeyDown={handleDecrementKeyDown}
              type="button"
            >
              <CoreIcon name="icon-action-minus" ariaHidden={true} />
            </button>
            <button
              id={`${id}-increment`}
              data-testid={`${id}-increment`}
              className={clsx(
                styles['core-button'],
                styles['core-button-tertiary'],
                styles['core-button-leading']
              )}
              aria-label={incrementAriaLabel}
              onClick={incrementClicked}
              onKeyDown={handleIncrementKeyDown}
              type="button"
            >
              <CoreIcon name="icon-action-plus" ariaHidden={true} />
            </button>
          </>
        )}
      </div>
      <div className={`${styles['core-form-supporting-text']}`}>
        {!errorText && helpText && (
          <div
            id={`${id}-help-text`}
            data-testid={`${id}-help-text`}
            className={`${styles['core-form-supporting-text-message']}`}
          >
            {helpText}
          </div>
        )}
        {errorText && (
          <div
            aria-live="polite"
            className={`${styles['form-field-error-container']}`}
          >
            <div
              id={`${id}-error-text`}
              data-testid={`${id}-error-text`}
              className={clsx(
                styles['core-form-supporting-text-error'],
                styles['error-message']
              )}
            >
              <CoreIcon
                size="small"
                ariaHidden={true}
                name="icon-status-error-fill"
              />
              {errorText}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoreTextField;
